import React from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { motion } from 'framer-motion';
import { Smartphone, MapPin, Lock, Zap, Wind, Key, Shield } from 'lucide-react';
interface HeroProps {
  onNavigate: (page: string) => void;
}
export function Hero({
  onNavigate
}: HeroProps) {
  const features = [{
    icon: Lock,
    text: 'Türen öffnen'
  }, {
    icon: Zap,
    text: 'Motor Start'
  }, {
    icon: Wind,
    text: 'Klima steuern'
  }, {
    icon: MapPin,
    text: 'Ortung'
  }];
  return <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-10" data-id="element-147">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-20" data-id="element-148" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/80 via-[#0a0a0f]/90 to-[#0a0a0f]" data-id="element-149" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-[#0a0a0f]/0 to-[#0a0a0f]/0" data-id="element-150" />

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" data-id="element-151" />

      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-152">
        <div className="max-w-4xl mx-auto text-center" data-id="element-153">
          {/* USP Badges */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="flex flex-wrap justify-center gap-3 mb-8" data-id="element-154">
            <Badge variant="accent" className="py-1.5 px-4" data-id="element-155">
              <Zap className="w-3 h-3 mr-2" data-id="element-156" /> Remote Start
            </Badge>
            <Badge variant="accent" className="py-1.5 px-4" data-id="element-157">
              <MapPin className="w-3 h-3 mr-2" data-id="element-158" /> GPS Ortung
            </Badge>
            <Badge variant="accent" className="py-1.5 px-4" data-id="element-159">
              <Lock className="w-3 h-3 mr-2" data-id="element-160" /> Auto Lock/Unlock
            </Badge>
          </motion.div>

          {/* Headlines */}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight" data-id="element-161">
            Der digitale Autoschlüssel – <br data-id="element-162" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600 text-glow" data-id="element-163">
              sicher, intelligent, vernetzt
            </span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-164">
            Steuern Sie Ihr Fahrzeug per Smartphone: Türen öffnen, Motor
            starten, Standort finden, Klima aktivieren und Schlüssel sicher
            teilen – per Bluetooth oder 4G.
          </motion.p>

          {/* CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-165">
            <Button variant="primary" size="lg" className="w-full sm:w-auto min-w-[200px]" onClick={() => onNavigate('contact')} data-id="element-166">
              Demo anfragen
            </Button>
            <Button variant="outline" size="lg" className="w-full sm:w-auto min-w-[200px]" onClick={() => onNavigate('product')} data-id="element-167">
              Produkt entdecken
            </Button>
          </motion.div>

          {/* Quick Features */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto" data-id="element-168">
            {features.map((feature, index) => <div key={index} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10" data-id="element-169">
                <feature.icon className="w-6 h-6 text-cyan-400" data-id="element-170" />
                <span className="text-sm font-medium text-gray-300" data-id="element-171">
                  {feature.text}
                </span>
              </div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}